import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import bookingService from '../../services/bookingService';
import webSocketService from '../../services/webSocketService';
import { API_CONFIG } from '../../config/api';
import { Loader2, MapPin, Navigation, Clock, Phone, User, RefreshCw, Car, Building2, Truck, AlertCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import TrackingMap from '../maps/TrackingMap';

const TrackBookings = ({ initialBookingId }) => {
  const { user } = useAuth();
  const [activeBookings, setActiveBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [websocketLocation, setWebsocketLocation] = useState(null);

  // Get booking ID for real-time tracking
  const bookingId = selectedBooking?.booking_id || selectedBooking?.id;

  // Clear tracking data when switching bookings to prevent merging
  useEffect(() => {
    if (!bookingId) {
      setTrackingData(null);
      setWebsocketLocation(null);
    }
  }, [bookingId]);

  // Handle booking selection with state cleanup
  const handleBookingSelect = (booking) => {
    setTrackingData(null);
    setWebsocketLocation(null);
    setSelectedBooking(booking);
  };

  // Auto-select initial booking if provided
  useEffect(() => {
    if (initialBookingId && activeBookings.length > 0) {
      const matched = activeBookings.find(b => String(b.booking_id || b.id) === String(initialBookingId));
      if (matched) {
        handleBookingSelect(matched);
      }
    }
  }, [initialBookingId, activeBookings]);

  // WebSocket integration
  useEffect(() => {
    const key = API_CONFIG?.STORAGE_KEYS?.JWT_TOKEN || 'jwt_token';
    const token = localStorage.getItem(key) || localStorage.getItem('aps_jwt_token');

    if (token && !webSocketService.isConnected) {
      webSocketService.connect(token);
    }

    if (!bookingId) return;

    webSocketService.joinBookingRoom(bookingId);

    const handleDriverLocationUpdated = (data) => {
      const eventBookingId = data.bookingId || data.booking_id;

      if (String(eventBookingId) === String(bookingId)) {
        const tracking = data.tracking || data;
        const loc = tracking.driverLocation || tracking;

        const lat = parseFloat(loc.latitude || loc.lat);
        const lng = parseFloat(loc.longitude || loc.lng || loc.lon);

        if (!isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng) &&
          lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {

          setWebsocketLocation({
            latitude: lat,
            longitude: lng,
            heading: loc.heading || null,
            speed: loc.speed || null,
            accuracy: loc.accuracy || null,
            lastUpdated: new Date().toISOString()
          });

          if (tracking.eta || tracking.distance) {
            setTrackingData(prev => ({
              ...prev,
              tracking: {
                ...prev?.tracking,
                eta: tracking.eta || prev?.tracking?.eta,
                distance: tracking.distance || prev?.tracking?.distance
              }
            }));
          }
        }
      }
    };

    webSocketService.on('driver_location_updated', handleDriverLocationUpdated);
    webSocketService.on('driver_location_update', handleDriverLocationUpdated);

    return () => {
      webSocketService.leaveBookingRoom(bookingId);
      webSocketService.off('driver_location_updated', handleDriverLocationUpdated);
      webSocketService.off('driver_location_update', handleDriverLocationUpdated);
    };
  }, [bookingId]);

  const fetchActiveBookings = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      const response = await bookingService.getAllBookings({ limit: 100 });

      if (response.success || Array.isArray(response) || response.data) {
        let allBookings = response.data?.bookings || response.bookings || response.data || response;
        if (!Array.isArray(allBookings)) allBookings = [];

        setActiveBookings(allBookings);

        if (allBookings.length > 0) {
          if (initialBookingId) {
            const matched = allBookings.find(b => String(b.booking_id || b.id) === String(initialBookingId));
            if (matched) {
              handleBookingSelect(matched);
            } else if (!selectedBooking) {
              handleBookingSelect(allBookings[0]);
            }
          } else if (!selectedBooking) {
            handleBookingSelect(allBookings[0]);
          }
        }
      }
    } catch (err) {
      console.error('❌ Error fetching bookings:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const fetchTrackingData = useCallback(async (id) => {
    if (!id) return;

    try {
      setTrackingLoading(true);
      const response = await bookingService.getLiveTrackingData(id);

      if (response && (response.success || response.data)) {
        const data = response.data || response;
        setTrackingData(data);
      }
    } catch (err) {
      console.error('❌ Error fetching tracking data:', err);
    } finally {
      setTrackingLoading(false);
    }
  }, []);

  const refreshTracking = async () => {
    setRefreshing(true);
    await fetchActiveBookings();
    if (selectedBooking) {
      await fetchTrackingData(selectedBooking.booking_id || selectedBooking.id);
    }
    setRefreshing(false);
  };

  useEffect(() => {
    fetchActiveBookings();
  }, [fetchActiveBookings]);

  useEffect(() => {
    if (bookingId) {
      fetchTrackingData(bookingId);
      const interval = setInterval(() => fetchTrackingData(bookingId), 30000);
      return () => clearInterval(interval);
    }
  }, [bookingId, fetchTrackingData]);


  // --- UI Helpers ---

  const getStatusBadge = (status) => {
    const statusConfig = {
      'CONFIRMED': { color: 'bg-blue-100 text-blue-800', text: 'Confirmed' },
      'ASSIGNED': { color: 'bg-purple-100 text-purple-800', text: 'Assigned' },
      'IN_PROGRESS': { color: 'bg-orange-100 text-orange-800', text: 'In Progress' },
      'COMPLETED': { color: 'bg-green-100 text-green-800', text: 'Completed' },
      'CANCELLED': { color: 'bg-red-100 text-red-800', text: 'Cancelled' },
      'PENDING': { color: 'bg-yellow-100 text-yellow-800', text: 'Pending' }
    };
    const config = statusConfig[status?.toUpperCase()] || { color: 'bg-gray-100 text-gray-800', text: status };
    return <Badge className={config.color}>{config.text}</Badge>;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };


  // --- Location Extractors ---

  const pickupLocation = useMemo(() => {
    const data = trackingData?.pickupLocation || trackingData?.pickup_location || selectedBooking?.pickup_location || selectedBooking?.pickupLocation;
    if (data && (data.latitude || data.lat)) {
      return {
        latitude: parseFloat(data.latitude || data.lat),
        longitude: parseFloat(data.longitude || data.lng || data.lon),
        address: data.address || selectedBooking?.pickup_address
      }
    }
    if (selectedBooking && selectedBooking.pickup_latitude && selectedBooking.pickup_longitude) {
      return {
        latitude: parseFloat(selectedBooking.pickup_latitude),
        longitude: parseFloat(selectedBooking.pickup_longitude),
        address: selectedBooking.pickup_address
      }
    }
    return null;
  }, [trackingData, selectedBooking]);

  const dropoffLocation = useMemo(() => {
    const data = trackingData?.dropoffLocation || trackingData?.dropoff_location || selectedBooking?.dropoff_location || selectedBooking?.dropoffLocation;
    if (data && (data.latitude || data.lat)) {
      return {
        latitude: parseFloat(data.latitude || data.lat),
        longitude: parseFloat(data.longitude || data.lng || data.lon),
        address: data.address || selectedBooking?.dropoff_address
      }
    }
    if (selectedBooking && selectedBooking.dropoff_latitude && selectedBooking.dropoff_longitude) {
      return {
        latitude: parseFloat(selectedBooking.dropoff_latitude),
        longitude: parseFloat(selectedBooking.dropoff_longitude),
        address: selectedBooking.dropoff_address
      }
    }
    return null;
  }, [trackingData, selectedBooking]);

  const ambulanceLocation = useMemo(() => {
    if (websocketLocation) return websocketLocation;

    const loc = trackingData?.tracking?.driverLocation || trackingData?.driverLocation || trackingData?.ambulance_location;
    if (loc && (loc.latitude || loc.lat)) {
      return {
        latitude: parseFloat(loc.latitude || loc.lat),
        longitude: parseFloat(loc.longitude || loc.lng || loc.lon),
        heading: loc.heading,
        lastUpdated: loc.lastUpdated
      }
    }
    return null;
  }, [websocketLocation, trackingData]);

  if (loading && !activeBookings.length) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading bookings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Live Tracking</h1>
          <p className="text-gray-600">Monitor active ambulance bookings in real-time</p>
        </div>

        <div className="flex items-center gap-3">
          <Button
            onClick={refreshTracking}
            disabled={refreshing}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Bookings List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow p-6">
            <h2 className="text-lg font-semibold mb-4">
              Active Bookings ({activeBookings.length})
            </h2>

            {activeBookings.length === 0 ? (
              <div className="text-center py-8">
                <Navigation className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Bookings</h3>
                <p className="text-gray-500 text-sm">No active bookings available.</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
                {activeBookings.map((booking) => {
                  const bId = booking.booking_id || booking.id;
                  return (
                    <div
                      key={bId}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${(selectedBooking?.booking_id === bId || selectedBooking?.id === bId)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                        }`}
                      onClick={() => handleBookingSelect(booking)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">#{bId}</span>
                        {getStatusBadge(booking.status)}
                      </div>

                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2">
                          <User className="w-3 h-3 text-gray-400" />
                          <span className="text-gray-700 truncate">{booking.patient_name || 'Unknown'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-3 h-3 text-gray-400" />
                          <span className="text-gray-500">{formatTime(booking.created_at)}</span>
                        </div>

                        {/* Source Tags - Kept discreetly as they provide important context for Admins */}
                        <div className="flex flex-wrap gap-2 mt-2">
                          {(booking.operator_id || booking.operator || booking.operatorId) && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                              <Truck className="w-3 h-3 mr-1" /> Fleet
                            </span>
                          )}
                          {(booking.hospital_id || booking.hospital || booking.hospitalId) && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                              <Building2 className="w-3 h-3 mr-1" /> Hospital
                            </span>
                          )}
                        </div>

                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        {/* Map & Details Area - EXACT UI MATCHING FLEET DASHBOARD */}
        <div className="lg:col-span-2">
          {!selectedBooking ? (
            <div className="bg-white rounded-xl shadow p-6 text-center">
              <Navigation className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Booking</h3>
              <p className="text-gray-500">Choose an active booking from the list to view tracking details.</p>
            </div>
          ) : (
            <div className="space-y-6">

              {/* CARD 1: Booking Details */}
              <div className="bg-white rounded-xl shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Booking #{selectedBooking.booking_id || selectedBooking.id}
                  </h2>
                  {getStatusBadge(selectedBooking.status)}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-gray-400" />
                    <div>
                      <span className="text-sm font-medium text-gray-700">Patient</span>
                      <p className="text-gray-900">{selectedBooking.patient_name || 'Unknown'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-gray-400" />
                    <div>
                      <span className="text-sm font-medium text-gray-700">Contact</span>
                      <p className="text-gray-900">{selectedBooking.patient_phone || selectedBooking.contact_number || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-gray-400" />
                    <div>
                      <span className="text-sm font-medium text-gray-700">Booking Time</span>
                      <p className="text-gray-900">{formatDate(selectedBooking.created_at)} at {formatTime(selectedBooking.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Car className="w-5 h-5 text-gray-400" />
                    <div>
                      <span className="text-sm font-medium text-gray-700">Ambulance</span>
                      <p className="text-gray-900">
                        {trackingData?.vehicle?.plate_number || selectedBooking.vehicle?.plate_number || selectedBooking.ambulance_number || 'Not assigned'}
                      </p>
                      {(trackingData?.vehicle?.ambulance_type || selectedBooking.vehicle?.type?.name) && (
                        <p className="text-sm text-gray-500">
                          {trackingData?.vehicle?.ambulance_type || selectedBooking.vehicle?.type?.name}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* CARD 2: Location Details */}
              <div className="bg-white rounded-xl shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Location Details</h3>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-green-700">Pickup Location</span>
                      <p className="text-gray-900 mt-1">
                        {selectedBooking.pickup_location?.address ||
                          selectedBooking.pickup_address ||
                          (typeof selectedBooking.pickup_location === 'string' ? selectedBooking.pickup_location : null) ||
                          'Not specified'}
                      </p>
                    </div>
                  </div>

                  {(selectedBooking.dropoff_location || selectedBooking.drop_location) && (
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-red-700">Drop Location</span>
                        <p className="text-gray-900 mt-1">
                          {selectedBooking.dropoff_location?.address ||
                            selectedBooking.dropoff_address ||
                            selectedBooking.drop_location?.address ||
                            selectedBooking.drop_location ||
                            'Not specified'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Driver Current Location */}
                  {ambulanceLocation && (
                    <div className="flex items-start gap-3 pt-3 border-t border-gray-100">
                      <Navigation className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-blue-700">Driver Current Location</span>
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs">
                            <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                            Live
                          </span>
                        </div>
                        <p className="text-gray-900 mt-1 font-mono text-sm">
                          {ambulanceLocation.latitude?.toFixed(6)}, {ambulanceLocation.longitude?.toFixed(6)}
                        </p>
                        {ambulanceLocation.lastUpdated && (
                          <p className="text-gray-500 text-xs mt-1">
                            Last updated: {new Date(ambulanceLocation.lastUpdated).toLocaleTimeString()}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>


              {/* CARD 3: Live Tracking Map */}
              <div className="bg-white rounded-xl shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Live Tracking</h3>
                  <div className="flex items-center gap-2">
                    {trackingLoading && (
                      <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    )}
                    {/* Location Status Indicator */}
                    <div className="flex items-center gap-2 text-xs">
                      <div className={`flex items-center gap-1 px-2 py-1 rounded ${pickupLocation ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                        <MapPin className="w-3 h-3" />
                        <span>Pickup</span>
                      </div>
                      <div className={`flex items-center gap-1 px-2 py-1 rounded ${dropoffLocation ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        <MapPin className="w-3 h-3" />
                        <span>Dropoff</span>
                      </div>
                      <div className={`flex items-center gap-1 px-2 py-1 rounded ${ambulanceLocation ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}>
                        <Navigation className="w-3 h-3" />
                        <span>Driver</span>
                      </div>
                    </div>
                  </div>
                </div>

                {!trackingData ? (
                  <div className="text-center py-8">
                    <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <h4 className="text-lg font-medium text-gray-900 mb-2">No Tracking Data</h4>
                    <p className="text-gray-500">Tracking information will appear here once the ambulance is en route.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Map Component */}
                    {(pickupLocation || dropoffLocation || ambulanceLocation) ? (
                      <div className="relative h-64 sm:h-80 lg:h-96 rounded-lg border border-gray-200 overflow-hidden bg-gray-100">
                        <TrackingMap
                          key={bookingId || 'new'}
                          pickupLocation={pickupLocation}
                          dropoffLocation={dropoffLocation}
                          ambulanceLocation={ambulanceLocation}
                          distance={trackingData?.tracking?.distance}
                          eta={trackingData?.tracking?.eta}
                          status={trackingData?.status || selectedBooking?.status}
                          journeyDirection={selectedBooking?.journey_direction || selectedBooking?.journeyDirection || selectedBooking?.direction || 'OUTBOUND'}
                          zoom={13}
                          showControls={true}
                          height="100%"
                        />
                        {/* Live tracking indicator */}
                        {ambulanceLocation && (
                          <div className="absolute top-2 left-2 flex items-center space-x-2 px-3 py-1.5 rounded-lg text-xs font-medium z-30 shadow-lg bg-blue-500 text-white">
                            <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
                            <span>LIVE TRACKING</span>
                          </div>
                        )}
                        {/* Last update timestamp */}
                        {ambulanceLocation?.lastUpdated && (
                          <div className="absolute bottom-2 left-2 bg-white/90 backdrop-blur-sm text-gray-600 px-2 py-1 rounded text-xs font-medium z-30 border border-gray-200">
                            Updated: {formatTime(ambulanceLocation.lastUpdated)}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                        <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <h4 className="text-lg font-medium text-gray-900 mb-2">No Location Data Available</h4>
                        <p className="text-gray-500 text-sm mb-4">Waiting for location data to be available.</p>
                        <div className="text-xs text-gray-400 space-y-1">
                          <p>Pickup: {pickupLocation ? '✅' : '❌'}</p>
                          <p>Dropoff: {dropoffLocation ? '✅' : '❌'}</p>
                          <p>Driver: {ambulanceLocation ? '✅' : '❌'}</p>
                        </div>
                      </div>
                    )}

                    {/* Tracking Information */}
                    <div className="space-y-4">
                      {trackingData.current_location && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <span className="text-sm font-medium text-gray-700">Current Location</span>
                            <p className="text-gray-900 mt-1">{trackingData.current_location}</p>
                          </div>

                          {trackingData.eta && (
                            <div>
                              <span className="text-sm font-medium text-gray-700">Estimated Arrival</span>
                              <p className="text-gray-900 mt-1">{trackingData.eta}</p>
                            </div>
                          )}
                        </div>
                      )}

                      {trackingData.driver && (
                        <div className="border-t pt-4">
                          <span className="text-sm font-medium text-gray-700">Driver Information</span>
                          <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <p className="text-gray-900">{trackingData.driver.name || 'Unknown Driver'}</p>
                              <p className="text-sm text-gray-500">{trackingData.driver.phone || 'No contact'}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {trackingData.last_updated && (
                        <div className="text-xs text-gray-500 border-t pt-2">
                          Last updated: {formatTime(trackingData.last_updated)}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrackBookings;